"use client";

import type { PortalFaq } from "@/actions/faq/fetchPortalFaqs";
import Dropdown from "@/components/shared/Dropdown";
import { PageTransition } from "@/layouts/transitions";
import {
  type DefaultPortalFaqCategory,
  getFaqCategories,
  getFaqCategoryLabel,
  getFaqCategoryLabels,
  isDefaultFaqCategory,
} from "@/lib/faqCategories";
import { useLocale, useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

const fallbackCounts: Record<DefaultPortalFaqCategory, number> = {
  general: 6,
  order: 12,
  costs: 8,
  design: 4,
  company: 2,
  affiliate: 10,
};

type FaqDropdownItem = {
  value: string;
  trigger: string;
  content: ReactNode;
};

const FaqHolder = ({ managedFaqs = [] }: { managedFaqs?: PortalFaq[] }) => {
  const t = useTranslations("common");
  const tFaq = useTranslations("faq");
  const locale = useLocale();

  const [page, setPage] = useState(0);
  const hasManagedFaqs = managedFaqs.length > 0;
  const categories = useMemo(
    () => getFaqCategories(managedFaqs),
    [managedFaqs],
  );

  useEffect(() => {
    if (page >= categories.length) {
      setPage(0);
    }
  }, [categories.length, page]);

  const topics = categories.map((category, index) => ({
    topic: getFaqCategoryLabel(
      category,
      t,
      getFaqCategoryLabels(managedFaqs, category),
      locale,
    ),
    page: index,
  }));

  const faqItemsByCategory = useMemo(() => {
    return categories.map((category) => {
      const managedItems = managedFaqs
        .filter((faq) => faq.category === category)
        .sort(
          (left, right) =>
            left.sort_order - right.sort_order || left.id - right.id,
        )
        .map((faq) => {
          const question = locale === "en" ? faq.question_en : faq.question_de;
          const answer = locale === "en" ? faq.answer_en : faq.answer_de;
          return {
            value: question,
            trigger: question,
            content: answer,
          };
        });

      if (managedItems.length > 0) {
        return managedItems;
      }

      if (hasManagedFaqs || !isDefaultFaqCategory(category)) {
        return [];
      }

      return Array.from({ length: fallbackCounts[category] }).map(
        (_, index) => {
          const suffix = index === 0 ? "" : String(index + 1);
          const question = tFaq(`${category}.question${suffix}`);
          return {
            value: question,
            trigger: question,
            content: tFaq(`${category}.answer${suffix}`),
          };
        },
      );
    });
  }, [categories, hasManagedFaqs, locale, managedFaqs, tFaq]);

  const activeFaqItems: FaqDropdownItem[] = faqItemsByCategory[page] ?? [];

  return (
    <div className="flex w-full flex-col justify-center gap-7 md:flex-row md:gap-10">
      <div className="hidden w-full md:block md:max-w-40 lg:max-w-xs">
        <ul className="flex flex-col">
          {topics.map((item, i) => (
            <li
              key={i}
              className={`${page === i ? "text-primary" : "text-ring"} ${page !== i ? "cursor-pointer" : "cursor-auto"} lg:hover:text-primary border-b py-4 text-xl transition-colors duration-150 last:border-0`}
              onClick={() => {
                setPage(item.page);
              }}
            >
              {item.topic}
            </li>
          ))}
        </ul>
      </div>

      <div className="m-auto block w-full md:hidden">
        <Dropdown
          triggerClassName="px-5 py-0"
          contentClassName="px-5 py-0 pb-0"
          items={[
            {
              value: topics[page].topic,
              trigger: topics[page].topic,
              content: topics.map((item, i) => (
                <div
                  key={i}
                  className={`${page === i ? "text-primary" : "text-ring"} ${page !== i ? "cursor-pointer" : "cursor-auto"} lg:hover:text-primary border-b py-3 text-xl transition-colors duration-150 last:border-0`}
                  onClick={() => {
                    setPage(item.page);
                  }}
                >
                  {item.topic}
                </div>
              )),
            },
          ]}
        />
      </div>

      <PageTransition key={page} className="flex w-full flex-col items-end">
        {activeFaqItems.length > 0 ? (
          <Dropdown
            className="bg-card shadow-lg"
            triggerClassName="px-5"
            contentClassName="text-ring px-5 pt-0 pb-4"
            items={activeFaqItems}
          />
        ) : (
          <div className="text-ring bg-card w-full px-5 py-4 shadow-lg">
            {tFaq("emptyCategory")}
          </div>
        )}
      </PageTransition>
    </div>
  );
};

export default FaqHolder;
